'use client';

/** PERFORMANCE — GSC site rows (same /api/seo/overview as classic, same
 *  per-project include/exclude scoping). Range pills, deltas, rec chips. */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { BusinessProject } from '@/lib/business-projects';
import { BUSINESS_PROJECTS } from '@/lib/business-projects';
import { Glass, Pill, Delta, StatusChip, HoloTable, Row, EmptyState, mono, display } from '@/future/primitives';
import { PerfDeckSurface } from './PerfDeck';
import type { ReactNode } from 'react';

/* ── PERFORMANCE HOME — LADDER ↔ FOCUS toggle (Boss 2026-07-10 experiment).
   LADDER = sortable email-style rows (this file); FOCUS = one-focus deck.
   Choice persists (bcc.perf.view). REVERT: tag pre-perf-ladder-experiment,
   or point SurfaceHost 'performance' back to PerfDeckSurface. */
export function PerfHome({ project, deckDomains }: { project: BusinessProject | null; deckDomains?: string[] }) {
  const [view, setView] = useState<'ladder' | 'focus'>('ladder');
  const [focusDomain, setFocusDomain] = useState<string | null>(null); // rec click → single-domain focus deck
  useEffect(() => { try { const v = localStorage.getItem('bcc.perf.view'); if (v === 'focus' || v === 'ladder') setView(v); } catch { } }, []);
  const flip = (v: 'ladder' | 'focus') => { setView(v); setFocusDomain(null); try { localStorage.setItem('bcc.perf.view', v); } catch { } };
  return (
    <div className="relative">
      {focusDomain
        ? <><div className="max-w-[1560px] mx-auto" style={{ marginBottom: 10 }}><Pill label={`◂ ALL SITES · ${focusDomain.toUpperCase()}`} active onClick={() => setFocusDomain(null)} /></div><PerfDeckSurface domains={[focusDomain]} /></>
        : view === 'ladder'
          ? <PerformanceSurface project={project} onRecommend={setFocusDomain} view={view} onFlip={flip} />
          : <><div className="max-w-[1560px] mx-auto flex gap-2" style={{ marginBottom: 10 }}>
              <Pill label="▤ LADDER" active={false} onClick={() => flip('ladder')} />
              <Pill label="▢ FOCUS" active onClick={() => flip('focus')} />
            </div><PerfDeckSurface domains={deckDomains} /></>}
    </div>
  );
}

type SiteRow = {
  domain: string; clicks: number; clicksChange: number | null; impressions: number;
  impressionsChange: number | null; avgPosition: number; positionChange: number | null;
  avgCtr: number; recommendation?: string; noData?: boolean;
};

const RANGES = ['7d', '14d', '28d', '3mo'];
/* the API window is a `days` integer (Boss 2026-07-11: range pills did nothing — the page sent
   ?range=7d but the route only reads ?days=N, so it always fell back to 28). Map pill → days. */
const RANGE_DAYS: Record<string, number> = { '7d': 7, '14d': 14, '28d': 28, '3mo': 90 };

/* LADDER sort keys — Boss 2026-07-10: 'sorted by clicks, see which ones perform better' */
const SORTS: { key: keyof SiteRow; label: string }[] = [
  { key: 'clicks', label: 'CLICKS' },
  { key: 'impressions', label: 'IMPRESSIONS' },
  { key: 'avgPosition', label: 'AVG POS' },
  { key: 'avgCtr', label: 'CTR' },
];

export function PerformanceSurface({ project, onRecommend, view, onFlip }: { project: BusinessProject | null; onRecommend?: (domain: string) => void; view?: 'ladder' | 'focus'; onFlip?: (v: 'ladder' | 'focus') => void }) {
  const [rows, setRows] = useState<SiteRow[]>([]);
  const [range, setRange] = useState('28d');
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<keyof SiteRow>('clicks');
  const [sortAsc, setSortAsc] = useState(false);
  useEffect(() => { try { const s = JSON.parse(localStorage.getItem('bcc.perf.sort') || 'null'); if (s) { setSortKey(s.k); setSortAsc(!!s.a); } } catch { } }, []);
  const setSort = (k: keyof SiteRow) => {
    const a = sortKey === k ? !sortAsc : (k === 'avgPosition'); // position: low = good → asc first
    setSortKey(k); setSortAsc(a);
    try { localStorage.setItem('bcc.perf.sort', JSON.stringify({ k, a })); } catch { }
  };
  const setSortExplicit = (k: keyof SiteRow, asc: boolean) => {
    setSortKey(k); setSortAsc(asc);
    try { localStorage.setItem('bcc.perf.sort', JSON.stringify({ k, a: asc })); } catch { }
  };
  /* VOICE CONTROL (Boss 2026-07-11 'I want to control the beast every single function with my
     voice'): the ladder answers sort + range commands. arg examples: 'clicks', 'impressions:asc',
     'position', 'ctr:desc', or a range like '7d'/'28d'. First surface wired for in-view voice control. */
  useEffect(() => {
    const KEYMAP: Record<string, keyof SiteRow> = {
      clicks: 'clicks', click: 'clicks', impressions: 'impressions', impression: 'impressions',
      position: 'avgPosition', 'avg position': 'avgPosition', rank: 'avgPosition',
      ctr: 'avgCtr', 'click through': 'avgCtr', 'click-through': 'avgCtr',
    };
    const on = (e: Event) => {
      const { intent, arg } = (e as CustomEvent).detail || {};
      if (intent !== 'sort' || !arg) return;
      const [rawKey, dir] = String(arg).toLowerCase().split(':');
      const key = KEYMAP[rawKey.trim()];
      const ok = (label?: string) => window.dispatchEvent(new CustomEvent('beast:voice-command-result', { detail: { intent: 'sort', ok: true, label } }));
      // a range word routed as sort ('sort ... 28 days') still adjusts the range
      if (!key && /^(7d|14d|28d|3mo)$/.test(rawKey.trim())) { setRange(rawKey.trim()); ok(rawKey.trim()); return; }
      if (!key) return;
      // default: clicks/impressions/ctr high→low (desc); position low→high (asc, best first)
      const asc = dir ? dir.startsWith('a') || dir.includes('low') : key === 'avgPosition';
      setSortExplicit(key, asc);
      ok(rawKey.trim());
    };
    window.addEventListener('beast:voice-command', on);
    return () => window.removeEventListener('beast:voice-command', on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* VOICE FOCUS (Boss 2026-07-11 'open cobbler'): the hub answers an 'open <site>' command by
     focusing that domain's deck. A site lives here (factory), not as a top-level card, so this is
     where 'open cobbler' actually lands. Claims the command with an ok result so the orb confirms. */
  const rowsRef = useRef<SiteRow[]>([]);
  rowsRef.current = rows;
  useEffect(() => {
    const on = (e: Event) => {
      const { intent, arg } = (e as CustomEvent).detail || {};
      if (intent !== 'open' || !arg || !onRecommend) return;
      const q = String(arg).toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!q) return;
      const found = rowsRef.current.find(r => {
        const d = r.domain.toLowerCase().replace(/[^a-z0-9]/g, '');
        return d === q || d.startsWith(q) || d.replace(/(ca|com|fr|net|org|film|io)$/, '') === q;
      });
      if (found) { onRecommend(found.domain); window.dispatchEvent(new CustomEvent('beast:voice-command-result', { detail: { intent: 'open', ok: true, label: found.domain } })); }
    };
    window.addEventListener('beast:voice-command', on);
    return () => window.removeEventListener('beast:voice-command', on);
  }, [onRecommend]);

  const scope = useMemo(() => {
    if (!project) return {};
    if (project.sites !== 'factory') return { include: project.sites.map(s => s.domain) };
    const exclude = BUSINESS_PROJECTS.flatMap(p => (p.slug !== project.slug && p.sites !== 'factory' ? p.sites.map(s => s.domain) : []));
    return { exclude };
  }, [project]);

  useEffect(() => {
    let live = true;
    const load = () => fetch(`/api/seo/overview?days=${RANGE_DAYS[range] ?? 28}`)
      .then(r => r.json())
      .then((data: SiteRow[]) => {
        if (!live || !Array.isArray(data)) return;
        let r2 = data;
        if (scope.include) r2 = r2.filter(s => scope.include!.includes(s.domain));
        else if (scope.exclude?.length) r2 = r2.filter(s => !scope.exclude!.includes(s.domain));
        setRows(r2);
        setLoading(false);
      }).catch(() => setLoading(false));
    load();
    const i = setInterval(load, 5 * 60_000); // live-stats law: 5min silent refetch
    return () => { live = false; clearInterval(i); };
  }, [range, scope]);

  // same rule-based recommendation as classic /seo (single source of logic drift risk — P7 extracts to shared lib)
  const recommend = (s: SiteRow): { text: string; kind: 'good' | 'warn' | 'bad' | 'dim' } => {
    if (s.noData) return { text: 'JUST ADDED — DATA IN A FEW DAYS', kind: 'dim' };
    if ((s.clicksChange ?? 0) <= -25) return { text: 'TRAFFIC DROPPING — INVESTIGATE TOP QUERIES', kind: 'bad' };
    if (s.avgPosition > 40) return { text: 'LOW VISIBILITY — STRENGTHEN CONTENT & LINKS', kind: 'bad' };
    if (s.avgPosition <= 10 && s.avgCtr < 3) return { text: 'PAGE 1 BUT WEAK CTR — IMPROVE TITLES & META', kind: 'warn' };
    if (s.avgPosition > 10 && s.avgPosition <= 20) return { text: 'PUSH PAGE-2 KEYWORDS TO PAGE 1', kind: 'warn' };
    if (s.impressions > 10000 && s.avgCtr < 1) return { text: 'HIGH IMPRESSIONS, FEW CLICKS — CTR OPPORTUNITY', kind: 'warn' };
    return { text: 'HEALTHY — KEEP PUBLISHING', kind: 'good' };
  };

  /* sorted ladder — no-data rows always sink to the bottom */
  const sorted = useMemo(() => {
    const withData = rows.filter(r => !r.noData), without = rows.filter(r => r.noData);
    withData.sort((a, b) => {
      const av = (a[sortKey] as number) ?? 0, bv = (b[sortKey] as number) ?? 0;
      return sortAsc ? av - bv : bv - av;
    });
    return [...withData, ...without];
  }, [rows, sortKey, sortAsc]);

  return (
    <div className="max-w-[1560px] mx-auto space-y-4" style={{ ['--uif-cols' as any]: '2.2fr 1fr 1fr 1fr .8fr 2fr' }}>
      {/* ONE solid sticky header (Boss 2026-07-11 'the top is messy, I can still see through' —
          replaced two seamed sticky bars with a single opaque block: no seam, nothing bleeds through.
          Row 1: view toggle + range window + site count. Row 2: column labels over their columns. */}
      <div style={{ position: 'sticky', top: 0, zIndex: 6, padding: '2px 12px 8px', margin: '0 -12px', background: '#050d14', borderBottom: '1px solid rgba(125,232,255,.14)', boxShadow: '0 10px 20px -8px #050d14, 0 -30px 0 0 #050d14' }}>
        {/* row 1 — view toggle (left) · range window · site count (right) */}
        <div className="flex items-center gap-2">
          {onFlip && <>
            <Pill label="▤ LADDER" active={view === 'ladder'} onClick={() => onFlip('ladder')} />
            <Pill label="▢ FOCUS" active={view === 'focus'} onClick={() => onFlip('focus')} />
            <span style={{ width: 1, height: 20, background: 'rgba(125,232,255,.16)', margin: '0 6px' }} />
          </>}
          {RANGES.map(r => <Pill key={r} label={r.toUpperCase()} active={range === r} onClick={() => setRange(r)} />)}
          <span className="ml-auto" style={{ ...mono(16, '.2em'), color: 'var(--uif-t5)' }}>{rows.length} SITES · GOOGLE SEARCH CONSOLE</span>
        </div>
        {/* row 2 — column headers aligned OVER their number columns (Boss 2026-07-11 'the labels
            don't match the columns'): mirrors the data-row box model exactly — same gap 16, same
            widths (rank 34 · domain flex · four 150 cells · proposal 440). Each label is the sort toggle. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 23px 4px' }}>
          <span style={{ width: 34, flex: 'none' }} />
          <span style={{ flex: '1 1 auto', minWidth: 280, ...mono(13, '.24em'), color: 'var(--uif-t6)' }}>SITE</span>
          {SORTS.map(s => (
            <button key={s.key as string} onClick={() => setSort(s.key)}
              style={{ width: 150, flex: 'none', textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', ...mono(13, '.18em'), color: sortKey === s.key ? 'var(--uif-cy-numeral)' : 'var(--uif-t5)' }}>
              {s.label} {sortKey === s.key ? (sortAsc ? '▴' : '▾') : ''}
            </button>
          ))}
          <span style={{ width: 440, flex: 'none', textAlign: 'right', ...mono(13, '.18em'), color: 'var(--uif-t6)' }}>PROPOSAL</span>
        </div>
      </div>
      {/* EMAIL-STYLE LADDER (Boss 2026-07-10 'look how the email is done — same style, much less busy'):
          one airy single-line row per site — rank · domain · ONLY the selected metric with an inline
          delta · quiet one-line recommendation with a colored dot. No column wall, no chip spam. */}
      {/* floating rows — email-inbox law (Boss: 'do like the emails — separate, hover moves closer'):
         individual glass rows, 10px gaps, hover lifts toward the screen; all four metrics
         visible, the sorted one highlighted; recommendation is a button → domain focus deck */}
      <div className="uif-scanlines">
        {sorted.map((s, rank) => {
          const r = recommend(s);
          const dot = r.kind === 'good' ? '#5df2b4' : r.kind === 'warn' ? '#ffcf7d' : r.kind === 'bad' ? '#ff7d9c' : '#49677a';
          const cell = (key: keyof SiteRow, v: string, d: number | null) => (
            <span className="flex items-baseline" style={{ gap: 8, width: 150, flex: 'none' }}>
              <span style={{ ...display(sortKey === key ? 30 : 24, 300), color: sortKey === key ? 'var(--uif-cy-numeral)' : 'var(--uif-t3)' }}>{v}</span>
              {d != null && d !== 0 && <span style={{ ...mono(13), color: d > 0 ? '#5df2b4' : '#ff7d9c' }}>{d > 0 ? '▴' : '▾'}{Math.abs(Math.round(d))}%</span>}
            </span>
          );
          return (
            <div key={s.domain} className="uif-ladder-row" style={{
              display: 'flex', alignItems: 'center', gap: 16, minHeight: 62, boxSizing: 'border-box',
              padding: '6px 22px', borderRadius: 10, marginTop: rank === 0 ? 0 : 10,
              background: 'linear-gradient(170deg,rgba(40,140,180,.06),rgba(8,18,28,.42) 60%)',
              border: '1px solid rgba(125,232,255,.16)',
              transition: 'transform .22s cubic-bezier(.3,.8,.3,1), border-color .22s, box-shadow .22s',
            }}>
              <span style={{ ...mono(15, '.1em'), color: rank === 0 && !s.noData ? 'var(--uif-cy-numeral)' : 'var(--uif-t6)', width: 34, flex: 'none' }}>{s.noData ? '' : `#${rank + 1}`}</span>
              <span style={{ ...display(25, 400), color: 'var(--uif-t1)', flex: '1 1 auto', minWidth: 280, whiteSpace: 'nowrap' }}>{s.domain}</span>
              {s.noData ? (
                <span style={{ ...mono(15, '.14em'), color: 'var(--uif-t6)', width: 600, flex: 'none' }}>NO SEARCH CONSOLE DATA YET</span>
              ) : (
                <>
                  {cell('clicks', String(s.clicks), s.clicksChange ?? null)}
                  {cell('impressions', s.impressions?.toLocaleString() ?? '—', s.impressionsChange ?? null)}
                  {cell('avgPosition', String(s.avgPosition), s.positionChange != null ? -s.positionChange : null)}
                  {cell('avgCtr', `${s.avgCtr}%`, null)}
                </>
              )}
              <button onMouseUp={() => onRecommend?.(s.domain)} data-lock="LAUNCH"
                className="flex items-center text-left" style={{ gap: 9, width: 440, flex: 'none', justifyContent: 'flex-end', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flex: 'none', boxShadow: r.kind !== 'dim' ? `0 0 8px ${dot}` : 'none' }} />
                <span style={{ ...mono(15, '.1em'), color: 'var(--uif-t5)', whiteSpace: 'nowrap' }}>{r.text} ›</span>
              </button>
            </div>
          );
        })}
        {!loading && rows.length === 0 && <EmptyState what="search data" action="GOOGLE DATA LANDS A FEW DAYS AFTER SITE VERIFICATION" />}
        {loading && <div className="py-12 text-center" style={{ ...mono(17, '.3em'), color: 'var(--uif-t5)' }}>SYNCING TELEMETRY…</div>}
      </div>
      <style jsx global>{`
        .uif-ladder-row:hover {
          transform: translateY(-3px) scale(1.012);
          border-color: rgba(125,232,255,.45) !important;
          box-shadow: 0 14px 40px -14px rgba(0,0,0,.8), 0 0 34px -8px rgba(0,240,255,.28);
        }
      `}</style>
    </div>
  );
}
